/**
 * puzzle-product.js
 * Gestion de la page produit "Puzzle" — pièces uniques de collection.
 *
 * Flux panier      : clic bouton → lock → /cart/add.js
 * Flux réservation : clic bouton → lock → reservation
 *   Si LOCK_EXPIRED (> 5 min entre lock et submit) : message d'erreur, user recommence.
 *
 * Backend attendu :
 *   POST { type: 'check',       variantId } → { available } | { available, lockedAt, expiresAt }
 *   POST { type: 'lock',        variantId } → { success, lockedAt, expiresAt } | { success, code, message }
 *   POST { type: 'reservation', ...       } → { success, orderId? } | { success, code, message }
 *
 * Metafield : puzzle.locked_at (date_time) — data-locked-at en Unix seconds depuis Liquid.
 */

document.addEventListener('DOMContentLoaded', function () {
  var container = document.querySelector('[data-puzzle-product]');
  if (!container) return;

  // --- Configuration ---
  var productHandle = container.dataset.productHandle;
  var variantId     = parseInt(container.dataset.variantId, 10);
  var apiEndpoint   = container.dataset.apiEndpoint;
  var lockedAtRaw   = container.dataset.lockedAt || ''; // Unix seconds depuis Liquid

  console.log('[Puzzle] Init —', {
    productHandle: productHandle,
    variantId: variantId,
    apiEndpoint: !!apiEndpoint,
    lockedAt: lockedAtRaw || '(aucun)'
  });

  if (!apiEndpoint) {
    console.warn('[Puzzle] api_endpoint non configuré dans les settings de la section.');
  }

  // --- État ---
  var currentlyAvailable = computeAvailableFromLiquid(lockedAtRaw);
  var pollTimer          = null;
  var countdownTimer     = null; // countdown badge quand quelqu'un d'autre a le verrou
  var POLL_INTERVAL      = 30000; // 30 s

  // --- Références DOM ---
  var statusEl     = container.querySelector('[data-puzzle-status]');
  var resForm      = container.querySelector('[data-puzzle-reservation-form]');
  var resultEl     = container.querySelector('[data-puzzle-result]');
  var btnCart      = container.querySelector('[data-puzzle-action="cart"]');
  var btnRes       = container.querySelector('[data-puzzle-action="reservation"]');
  var tabs         = container.querySelectorAll('[data-puzzle-tab]');
  var fullFieldsEl = resForm ? resForm.querySelector('[data-puzzle-full-fields]') : null;

  // Stock = 0 → Liquid a rendu le bloc "déjà réservée" sans formulaire.
  // Pas de timer, pas de polling — on laisse l'état serveur tel quel.
  if (!resForm) return;

  // --- Init synchrone (évite le flash UI si verrou actif au chargement) ---
  if (!currentlyAvailable && lockedAtRaw) {
    var initialLockedAtMs = parseInt(lockedAtRaw, 10) * 1000;
    var initialExpiresAt  = new Date(initialLockedAtMs + 5 * 60 * 1000);
    setTemporarilyLocked(initialExpiresAt);
  }

  checkAvailability();
  if (currentlyAvailable) startPolling();
  bindTabs();
  bindActionButtons();

  // =========================================================
  // Calcul de disponibilité initiale (depuis data-locked-at)
  // =========================================================

  /** lockedAtStr = Unix seconds (string). Disponible si vide ou âge >= 5 min. */
  function computeAvailableFromLiquid(lockedAtStr) {
    if (!lockedAtStr) return true;
    var ms = parseInt(lockedAtStr, 10) * 1000;
    if (isNaN(ms) || ms <= 0) return true;
    return Date.now() - ms >= 5 * 60 * 1000;
  }

  // =========================================================
  // Check — POST { type: 'check' }
  // =========================================================

  function checkAvailability() {
    if (!apiEndpoint) return Promise.resolve(null);

    return callBackendRaw({ type: 'check', variantId: variantId })
      .then(function (data) {
        var wasAvailable = currentlyAvailable;
        currentlyAvailable = data.available;

        if (data.available) {
          stopCountdown();
          if (!wasAvailable) {
            console.log('[Puzzle] check — pièce à nouveau disponible');
            setAvailable();
            startPolling();
          }
        } else {
          var expiresAt = data.expiresAt ? new Date(data.expiresAt) : null;
          console.log('[Puzzle] check — verrouillée par quelqu\'un d\'autre, expire à', expiresAt);
          setTemporarilyLocked(expiresAt);
          if (wasAvailable) stopPolling();
        }

        return data.available;
      })
      .catch(function (err) {
        console.warn('[Puzzle] check — erreur (fail open) :', err.message);
        return null;
      });
  }

  // =========================================================
  // Polling + visibilitychange
  // =========================================================

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(function () {
      if (!currentlyAvailable) { stopPolling(); return; }
      checkAvailability();
    }, POLL_INTERVAL);
    document.addEventListener('visibilitychange', onVisibilityChange);
    console.log('[Puzzle] Polling démarré —', POLL_INTERVAL, 'ms');
  }

  function stopPolling() {
    if (!pollTimer) return;
    clearInterval(pollTimer);
    pollTimer = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    console.log('[Puzzle] Polling arrêté');
  }

  function onVisibilityChange() {
    if (!document.hidden && currentlyAvailable) {
      console.log('[Puzzle] Tab redevenu visible — check immédiat');
      checkAvailability();
    }
  }

  // =========================================================
  // Lock — POST { type: 'lock' }
  // =========================================================

  /**
   * Pose le verrou sur le variant.
   * @returns {Promise<boolean>} true = posé, false = déjà verrouillé par quelqu'un d'autre
   */
  function lockVariant() {
    if (!apiEndpoint) {
      console.warn('[Puzzle] lockVariant — api_endpoint manquant, skip (fail open)');
      return Promise.resolve(true);
    }

    console.log('[Puzzle] lockVariant → POST', apiEndpoint);

    return callBackendRaw({ type: 'lock', variantId: variantId })
      .then(function (data) {
        if (!data.success) {
          console.warn('[Puzzle] lockVariant — refusé :', data.code, data.message);
          if (data.code === 'ALREADY_LOCKED') {
            currentlyAvailable = false;
            stopPolling();
            checkAvailability(); // récupère expiresAt pour le countdown badge
          } else {
            showResult('error', data.message || 'Impossible de verrouiller. Veuillez réessayer.');
          }
          return false;
        }
        console.log('[Puzzle] lockVariant — succès, expire à', data.expiresAt);
        return true;
      })
      .catch(function (err) {
        console.warn('[Puzzle] lockVariant — erreur réseau (fail open) :', err.message);
        return true; // fail open
      });
  }

  // =========================================================
  // Onglets
  // =========================================================

  function bindTabs() {
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () { switchMode(tab.dataset.puzzleTab); });
    });
  }

  function switchMode(mode) {
    console.log('[Puzzle] switchMode —', mode);

    tabs.forEach(function (tab) {
      var active = tab.dataset.puzzleTab === mode;
      tab.classList.toggle('puzzle-tab--active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (fullFieldsEl) fullFieldsEl.hidden = (mode !== 'reservation');
    if (btnCart) btnCart.hidden = (mode !== 'cart');
    if (btnRes)  btnRes.hidden  = (mode !== 'reservation');
    container.querySelectorAll('[data-puzzle-mode-intro]').forEach(function (el) {
      el.hidden = (el.dataset.puzzleModeIntro !== mode);
    });
    if (resForm) {
      resForm.querySelectorAll('.puzzle-form__input--error').forEach(function (el) {
        el.classList.remove('puzzle-form__input--error');
      });
    }
    if (resultEl) resultEl.hidden = true;
  }

  // =========================================================
  // Boutons d'action — lock au clic, toujours
  // =========================================================

  function bindActionButtons() {
    if (btnCart) btnCart.addEventListener('click', function () { handleAction('cart'); });
    if (btnRes)  btnRes.addEventListener('click',  function () { handleAction('reservation'); });
  }

  function handleAction(action) {
    if (!validateForm(resForm)) return;
    setAllButtonsLoading(true);

    lockVariant().then(function (locked) {
      if (!locked) { setAllButtonsLoading(false); return; }

      // --- Panier : lock posé → ajout Shopify ---
      if (action === 'cart') {
        addToCart(variantId, buildPayload('cart'))
          .then(function (cartData) {
            currentlyAvailable = false;
            stopPolling();
            // Stocker le verrou pour que puzzle-cart-monitor.js puisse
            // le surveiller sur les autres pages (panier, expiry, retrait).
            try {
              localStorage.setItem('puzzle_lock', JSON.stringify({
                variantId:   variantId,
                lockedAt:    Date.now(),
                apiEndpoint: apiEndpoint
              }));
            } catch (e) {}
            console.log('[Puzzle] Panier mis à jour — redirect /cart', cartData);
            window.location.href = '/cart';
          })
          .catch(function (err) {
            console.error('[Puzzle] Erreur ajout panier :', err);
            setAllButtonsLoading(false);
            showResult('error', 'Impossible d\'ajouter au panier. Veuillez réessayer.');
          });
        return;
      }

      // --- Réservation : lock posé → backend (re-vérifie le verrou) ---
      if (action === 'reservation') {
        callBackendRaw(buildPayload('reservation'))
          .then(function (data) {
            if (!data.success) {
              setAllButtonsLoading(false);
              if (data.code === 'LOCK_EXPIRED') {
                showResult('error', 'Session expirée (> 5 min). Veuillez recommencer.');
              } else if (data.code === 'ALREADY_LOCKED' || data.code === 'OUT_OF_STOCK') {
                currentlyAvailable = false;
                stopPolling();
                setUnavailable('Cette pièce a été réservée entre-temps.');
              } else {
                showResult('error', data.message || 'Une erreur est survenue. Veuillez réessayer.');
              }
              return;
            }
            console.log('[Puzzle] Commande provisoire créée — orderId :', data.orderId);
            setAllButtonsLoading(false);
            currentlyAvailable = false;
            stopPolling();
            // Mettre à jour le badge statut
            if (statusEl) {
              statusEl.innerHTML = '<span class="puzzle-status__dot" aria-hidden="true"></span> Pièce déjà réservée';
              statusEl.className = 'puzzle-status puzzle-status--unavailable';
            }
            // Masquer l'ensemble onglets + formulaire
            var tabsContainer = container.querySelector('.puzzle-tabs');
            if (tabsContainer) tabsContainer.hidden = true;
            if (resForm) resForm.hidden = true;
            showResult('success', 'Votre commande provisoire est enregistrée ! Notre équipe vous contactera par email pour finaliser le paiement.');
          })
          .catch(function (err) {
            console.error('[Puzzle] Erreur réservation :', err);
            setAllButtonsLoading(false);
            showResult('error', 'Une erreur est survenue. Veuillez réessayer.');
          });
      }
    });
  }

  // =========================================================
  // Construction du payload
  // =========================================================

  function buildPayload(action) {
    var fd = new FormData(resForm);
    return {
      type:      action,
      variantId: variantId,
      firstName: (fd.get('firstName')  || '').trim(),
      lastName:  (fd.get('lastName')   || '').trim(),
      email:     (fd.get('email')      || '').trim(),
      company:   (fd.get('company')    || '').trim(),
      address1:  (fd.get('rue')        || '').trim(),
      zip:       (fd.get('codePostal') || '').trim(),
      city:      (fd.get('ville')      || '').trim(),
      engraving: (fd.get('engraving')  || '').trim()
    };
  }

  // =========================================================
  // Ajout au panier
  // =========================================================

  function addToCart(vId, payload) {
    var properties = {};
    if (payload.company)   properties['Entreprise'] = payload.company;
    if (payload.engraving) properties['Gravure']    = payload.engraving;

    console.log('[Puzzle] addToCart — variantId:', vId, 'properties:', properties);

    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ items: [{ id: vId, quantity: 1, properties: properties }] })
    }).then(function (r) {
      console.log('[Puzzle] addToCart ← HTTP', r.status);
      if (!r.ok) {
        return r.text().then(function (text) {
          var errBody = {};
          try { errBody = JSON.parse(text); } catch (e) {}
          throw new Error('Cart add HTTP ' + r.status + ' — ' + (errBody.description || errBody.message || text || 'Erreur inconnue'));
        });
      }
      return r.json();
    });
  }

  // =========================================================
  // Appel backend générique
  // =========================================================

  function callBackendRaw(payload) {
    console.log('[Puzzle] callBackendRaw → POST', apiEndpoint, '— type:', payload.type);
    return fetch(apiEndpoint, {
      method:  'POST',
      headers: { 'Content-Type': 'text/plain' },
      body:    JSON.stringify(payload)
    }).then(function (r) {
      console.log('[Puzzle] callBackendRaw ← HTTP', r.status);
      if (!r.ok && r.status !== 409) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  // =========================================================
  // Validation du formulaire
  // =========================================================

  function validateForm(form) {
    if (!form) return false;
    var valid = true;

    form.querySelectorAll('.puzzle-form__input').forEach(function (el) {
      el.classList.remove('puzzle-form__input--error');
    });
    form.querySelectorAll('[required]').forEach(function (field) {
      if (field.closest('[hidden]')) return;
      if (!field.value.trim()) { field.classList.add('puzzle-form__input--error'); valid = false; }
    });

    var emailField = form.querySelector('[name="email"]');
    if (emailField && !emailField.closest('[hidden]') && emailField.value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailField.value.trim())) {
        emailField.classList.add('puzzle-form__input--error');
        valid = false;
      }
    }

    if (!valid) {
      showResult('error', 'Veuillez remplir tous les champs obligatoires (*) correctement.');
      var firstError = form.querySelector('.puzzle-form__input--error');
      if (firstError) firstError.focus();
    }
    return valid;
  }

  // =========================================================
  // Utilitaires UI
  // =========================================================

  /** Réactive l'UI après expiration d'un verrou posé par quelqu'un d'autre. */
  function setAvailable() {
    stopCountdown();
    if (statusEl) {
      statusEl.innerHTML = '<span class="puzzle-status__dot" aria-hidden="true"></span> Pièce disponible — Exemplaire unique';
      statusEl.className = 'puzzle-status puzzle-status--available';
    }
    if (resForm) {
      resForm.hidden = false;
      resForm.querySelectorAll('input, textarea, button').forEach(function (el) {
        el.disabled = false;
      });
    }
    tabs.forEach(function (tab) { tab.disabled = false; });
    if (resultEl) resultEl.hidden = true;
  }

  /**
   * Badge orange + countdown : quelqu'un d'autre a le verrou.
   * Désactive le formulaire ; re-vérifie automatiquement à expiry.
   */
  function setTemporarilyLocked(expiresAt) {
    stopCountdown();

    if (resForm) {
      resForm.querySelectorAll('input, textarea, button').forEach(function (el) {
        el.disabled = true;
      });
    }
    tabs.forEach(function (tab) { tab.disabled = true; });

    if (!statusEl) return;

    function tick() {
      var remaining = expiresAt
        ? Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000))
        : 0;

      if (remaining === 0) {
        stopCountdown();
        checkAvailability();
        return;
      }

      var m = Math.floor(remaining / 60);
      var s = remaining % 60;
      var timeStr = m > 0 ? (m + 'min ' + (s < 10 ? '0' : '') + s + 's') : (s + 's');
      statusEl.innerHTML =
        '<span class="puzzle-status__dot" aria-hidden="true"></span>' +
        ' Pièce temporairement réservée — expire dans ' + timeStr;
      statusEl.className = 'puzzle-status puzzle-status--locked';
    }

    tick();
    countdownTimer = setInterval(tick, 1000);
  }

  function stopCountdown() {
    if (!countdownTimer) return;
    clearInterval(countdownTimer);
    countdownTimer = null;
  }

  /** Blocage définitif (vendu ou race condition). */
  function setUnavailable(message) {
    console.log('[Puzzle] setUnavailable —', message || '(sans message)');
    stopCountdown();

    if (statusEl) {
      statusEl.innerHTML = '<span class="puzzle-status__dot" aria-hidden="true"></span> Pièce déjà réservée';
      statusEl.className = 'puzzle-status puzzle-status--unavailable';
    }
    if (resForm) {
      resForm.querySelectorAll('input, textarea, button').forEach(function (el) {
        el.disabled = true;
      });
    }
    tabs.forEach(function (tab) { tab.disabled = true; });
    if (message) showResult('error', message);
  }

  function setAllButtonsLoading(loading) {
    if (loading) {
      if (btnCart && !btnCart.dataset.originalHtml) btnCart.dataset.originalHtml = btnCart.innerHTML;
      if (btnRes  && !btnRes.dataset.originalHtml)  btnRes.dataset.originalHtml  = btnRes.innerHTML;
    }
    [btnCart, btnRes].forEach(function (btn) {
      if (!btn) return;
      btn.disabled = loading;
      if (loading) btn.textContent = 'Traitement en cours…';
      else btn.innerHTML = btn.dataset.originalHtml || btn.textContent;
    });
  }

  function showResult(type, message) {
    if (!resultEl) return;
    resultEl.hidden = false;
    resultEl.className = 'puzzle-result puzzle-result--' + type;
    resultEl.textContent = message;
    resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
});
